import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Skeleton,
} from '@mui/material'
import { useContext, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import ReactMarkdown from 'react-markdown'
import { useFieldOptions } from '../../../hooks/data/useFieldOptions'
import { useRefData } from '../../../hooks/data/useRefData'
import { setDirtyForm } from '../../../services/appPreferences/dirtyFormStorage'
import AppContext from '../../../context/appContext'

const groupOptions = (options, field) => {
  if (!field.groupBy || !options?.length) return options
  const uniqueGroups = [...new Set(options.map(o => o[field.groupBy]).filter(Boolean))]
  return uniqueGroups.flatMap(groupValue => [
    { divider: true, label: field.groupLabels?.[groupValue] || groupValue },
    ...options.filter(o => o[field.groupBy] === groupValue),
  ])
}

const SelectWrapper = ({
  control,
  formField,
  rules,
  readOnly,
  fieldArrayName,
  fieldArrayIndex,
}) => {
  const { watch } = useFormContext()
  const { setPartialChanges, currentFarm } = useContext(AppContext)

  const { optionsFromField } = formField
  const sourceValues = optionsFromField?.sourceField ? watch(optionsFromField.sourceField) : null
  const filterValue = formField.filterBy
    ? fieldArrayName
      ? watch(`${fieldArrayName}.${fieldArrayIndex}.${formField.filterBy}`)
      : watch(formField.filterBy)
    : null

  const hasDefaultOptions = Boolean(formField.defaultOptions?.length)
  const refData = useRefData(
    optionsFromField || hasDefaultOptions ? undefined : formField.refDataKey,
  )

  const fieldOptions = useFieldOptions({ optionsFromField, sourceValues, farmId: currentFarm?.id })

  const options = useMemo(() => {
    const rawOptions = () => {
      if (optionsFromField) return fieldOptions.data
      if (hasDefaultOptions) return formField.defaultOptions
      if (!refData.data) return undefined
      return formField.filterBy
        ? refData.data.filter(option => option[formField.filterBy] === filterValue)
        : refData.data
    }

    return groupOptions(rawOptions(), formField)
  }, [optionsFromField, fieldOptions.data, hasDefaultOptions, formField, refData.data, filterValue])

  const isLoading = fieldOptions.isLoading || refData.isLoading

  if (isLoading) {
    return (
      <Skeleton
        height={56}
        sx={{
          borderRadius: 1,
          maxWidth: formField.maxWidth,
        }}
        variant="rectangular"
        width="100%"
      />
    )
  }

  return (
    options && (
      <Controller
        control={control}
        defaultValue={formField.defaultValue}
        name={formField.name}
        render={({ field, fieldState }) => (
          <FormControl disabled={readOnly} sx={{ width: '100%' }} variant="outlined">
            <InputLabel id={`${formField.name}-label`}>{formField.placeholder}</InputLabel>
            <Select
              defaultValue={formField.defaultValue}
              error={!!fieldState.error}
              fullWidth={formField.fullWidth}
              label={formField.placeholder}
              labelId={`${formField.name}-label`}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                PaperProps: {
                  sx: {
                    maxHeight: '20vh',
                    width: 150,
                  },
                },
              }}
              sx={{
                maxWidth: formField.maxWidth,
              }}
              value={field.value ?? ''}
              onChange={(_, change) => {
                _.preventDefault()
                setPartialChanges(true)
                field.onChange(change.props.value)
                if (!readOnly) setDirtyForm(true)
              }}
            >
              {options?.map(option => {
                return option.divider ? (
                  <ListSubheader key={option.label}>{option.label}</ListSubheader>
                ) : (
                  <MenuItem key={option.id ?? option.value} value={option.id ?? option.value}>
                    {option.es_AR ?? option.label}
                  </MenuItem>
                )
              })}
            </Select>
            <FormHelperText error={Boolean(fieldState.error)}>
              <ReactMarkdown>
                {fieldState.error ? fieldState.error.message : formField.helperText}
              </ReactMarkdown>
            </FormHelperText>
          </FormControl>
        )}
        rules={rules}
      />
    )
  )
}

export default SelectWrapper
